import { z } from 'zod';
/**
 * Payments (M7, ADR-010, `ARCHITECTURE.md` §10, `DATABASE.md` §7.7).
 *
 * Four nouns, and the separation between them is what stops a clinic charging the
 * wrong number:
 *
 * | Shape | Authority | Table |
 * |---|---|---|
 * | `PaymentMethodSummary` | the processor's vault | `payment_methods` |
 * | `ChargeIntent` | the rule engine, or a staff member with a reason | `charge_intents` |
 * | `Charge` | the processor | `charges` |
 * | `Refund` | the processor | `refunds` |
 *
 * A `ChargeIntent` is *what we mean to take*; a `Charge` is *what the processor did*.
 * They are never the same row, because a decline, a retry and a surcharge all make the
 * two differ, and a single row would have to lie about one of them.
 *
 * ## Nothing here is card data
 *
 * The furthest this file goes is a brand, four digits and an expiry - the three facts a
 * patient needs to recognise their own card. No PAN, no nonce, no vault token: the
 * `pm-…` id stays server-side, because a browser that holds it holds everything needed
 * to ask for a charge (`SECURITY.md` §8, FR-PAY-01).
 *
 * ## Nothing here is PHI, in either direction
 *
 * The processor is sent one opaque reference and nothing else (FR-PAY-05). That rule is
 * enforced in `packages/integrations/acceptblue/src/scrubber.ts`, but it starts here:
 * no shape below has a field an appointment's visit type, provider or date could be
 * written into.
 *
 * ## Amounts are integer cents, and the client never chooses one
 *
 * `amountCents` appears on a *staff* request and never on a public one (FR-PAY-07). The
 * patient's browser can save a card; it cannot say what the card is for.
 */
/* ------------------------------------------------------------ payment methods ---- */
export const PAYMENT_METHOD_STATUSES = ['active', 'expired', 'removed'];
export const PaymentMethodStatusSchema = z.enum(PAYMENT_METHOD_STATUSES);
/**
 * A vaulted card, as every surface is allowed to see it.
 *
 * `brand`, `last4` and the expiry are nullable because the processor does not always
 * return them (a reused vault entry created before this platform saw the card), and a
 * card we can charge but cannot describe is still a card we can charge. The UI renders
 * "Card on file" in that case rather than refusing to render.
 */
export const PaymentMethodSummarySchema = z
    .object({
    id: z.uuid(),
    brand: z.string().max(40).nullable().default(null),
    last4: z
        .string()
        .regex(/^\d{4}$/)
        .nullable()
        .default(null),
    expMonth: z.int().min(1).max(12).nullable().default(null),
    expYear: z.int().min(2000).max(2100).nullable().default(null),
    status: PaymentMethodStatusSchema,
    isDefault: z.boolean().default(false),
    createdAt: z.iso.datetime(),
})
    .strict();
/* --------------------------------------------------------------- public config ---- */
/**
 * `GET /public/payments/config` (M7.2, FR-PAY-01).
 *
 * The **only** vendor key that ever reaches a browser. Everything else an install holds
 * - the source key, the PIN, the webhook signature key - is a secret the browser has no
 * use for, so the shape has nowhere to put one.
 *
 * `scriptUrl` is served rather than hard-coded in the SPA for two reasons: the sandbox
 * and production hosts differ, and the patient app's CSP is built from what this
 * endpoint says, so one answer decides both which script may load and which script is
 * loaded (`INTEGRATIONS.md` §3).
 *
 * `enabled: false` is a complete, valid answer: a clinic that has not connected a
 * processor takes no cards, and the funnel skips the step rather than showing a broken
 * one.
 */
export const PaymentConfigSchema = z
    .object({
    enabled: z.boolean(),
    provider: z.literal('acceptblue').nullable().default(null),
    /** Public Hosted Tokenization key, `pk_…`. Null when the install has none yet. */
    publicKey: z.string().max(200).nullable().default(null),
    /** Hosted Tokenization script, exact URL. Null when `enabled` is false. */
    scriptUrl: z.url().max(2000).nullable().default(null),
    /** Sandbox credentials and sandbox host. The UI may badge the step with it. */
    sandbox: z.boolean().default(true),
})
    .strict();
export const PaymentConfigResponseSchema = z.object({ data: PaymentConfigSchema }).strict();
/* ---------------------------------------------------------------- vaulting ---- */
/**
 * The financial-agreement acceptance that goes with a saved card (FR-PAY-01, ADR-010).
 *
 * Captured with the card rather than at confirm because it is the card the patient is
 * agreeing about: "you may charge this card for my visit". The version is the document
 * the patient actually saw, so a later change to the wording cannot be applied
 * retroactively to a consent somebody gave to different words.
 */
export const PaymentConsentSchema = z
    .object({
    agreementVersion: z.string().min(1).max(40),
    acceptedAt: z.iso.datetime(),
})
    .strict();
/**
 * `POST /public/bookings/:id/payment-method`, `POST /public/drafts/current/payment-method`
 * and `POST /patient/payment-method`.
 *
 * The nonce is single use and lives fifteen minutes; it is exchanged for a vault entry
 * and never stored (FR-PAY-01). The expiry is in the body because accept.blue requires
 * it when the source is a nonce and there is no honest default - a guessed expiry is a
 * charge that declines months later for a reason nobody can reconstruct.
 *
 * `billingZip` is optional and is the only address-shaped value this platform will
 * accept here; it is used for AVS at vault time and is never stored.
 */
export const SavePaymentMethodRequestSchema = z
    .object({
    nonce: z.string().min(8).max(200),
    expiryMonth: z.int().min(1).max(12),
    expiryYear: z.int().min(2000).max(2100),
    billingZip: z
        .string()
        .regex(/^[0-9A-Za-z][0-9A-Za-z -]{2,9}$/)
        .optional(),
    consent: PaymentConsentSchema.optional(),
})
    .strict();
export const PaymentMethodResponseSchema = z
    .object({ data: z.object({ paymentMethod: PaymentMethodSummarySchema }).strict() })
    .strict();
/** `GET /patient/payment-method`: null before the patient has saved one. */
export const OptionalPaymentMethodResponseSchema = z
    .object({
    data: z.object({ paymentMethod: PaymentMethodSummarySchema.nullable() }).strict(),
})
    .strict();
/* ------------------------------------------------------------- charge intents ---- */
/**
 * What an intent is *for* (`charge_intents.kind`, `ck_charge_intents_kind`).
 *
 * `late_cancel` and `no_show` are the two fee kinds of FR-SCHED-10. They are separate
 * kinds rather than one `fee` kind because the partial unique index is on
 * `(tenant, appointment, kind)`: a visit that was charged and then no-showed after a
 * reschedule must be able to carry both rows, and a single `fee` kind would have made
 * the second one impossible to create without deleting the first.
 */
export const CHARGE_INTENT_KINDS = ['visit', 'late_cancel', 'no_show'];
export const ChargeIntentKindSchema = z.enum(CHARGE_INTENT_KINDS);
/**
 * The state machine (`ck_charge_intents_status`).
 *
 * ```
 *  awaiting_amount ─┐                      ┌─► voided
 *                   ├─► pending ─► scheduled ─► processing ─► charged ─┤
 *  (no amount yet)  │        ▲                              │          ├─► refunded
 *                   │        └──────────────────────────────┘          └─► partially_refunded
 *                   └─► cancelled                           └─► failed ─► (retry: pending)
 * ```
 *
 * - `awaiting_amount` is a real intent with no number yet: the rule engine said
 *   `pending_review`, or a fee is waiting for a staff member to confirm it. It is the
 *   state that keeps "we owe this patient a decision" visible instead of silently
 *   charging nothing.
 * - `processing` is held only while a vendor call is in flight. It is what makes the
 *   charge job exactly-once: a second worker finds `processing` and stops.
 * - `failed` and `cancelled` are outside the partial unique index, so a failed charge
 *   can be retried by creating a fresh intent without deleting the evidence.
 */
export const CHARGE_INTENT_STATUSES = [
    'awaiting_amount',
    'pending',
    'scheduled',
    'processing',
    'charged',
    'failed',
    'voided',
    'refunded',
    'partially_refunded',
    'cancelled',
];
export const ChargeIntentStatusSchema = z.enum(CHARGE_INTENT_STATUSES);
/** Where the number came from (`charge_intents.amount_source`). */
export const CHARGE_AMOUNT_SOURCES = ['rule_quote', 'staff_override', 'policy_fee'];
export const ChargeAmountSourceSchema = z.enum(CHARGE_AMOUNT_SOURCES);
export const ChargeIntentSchema = z
    .object({
    id: z.uuid(),
    appointmentId: z.uuid(),
    patientId: z.uuid(),
    paymentMethodId: z.uuid().nullable().default(null),
    kind: ChargeIntentKindSchema,
    status: ChargeIntentStatusSchema,
    /** Zero while `awaiting_amount`; the UI reads the status, not the number. */
    amountCents: z.int().min(0),
    currency: z.string().length(3),
    amountSource: ChargeAmountSourceSchema,
    priceQuoteId: z.uuid().nullable().default(null),
    /** Mandatory on a staff override, and the whole point of the row (FR-PAY-04). */
    overrideReason: z.string().max(500).nullable().default(null),
    overrideByUserId: z.string().max(128).nullable().default(null),
    scheduledAt: z.iso.datetime().nullable().default(null),
    attempts: z.int().min(0),
    /** A class, never the vendor's sentence: `card_declined`, `gateway_error`, … */
    lastErrorClass: z.string().max(80).nullable().default(null),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})
    .strict();
/* -------------------------------------------------------------------- charges ---- */
export const CHARGE_STATUSES = [
    'approved',
    'declined',
    'error',
    'settled',
    'voided',
    'refunded',
];
export const ChargeStatusSchema = z.enum(CHARGE_STATUSES);
/**
 * One processor transaction.
 *
 * `amountCents` is what the processor says it **authorised**, not what was asked for.
 * The two differ when the merchant has a mandatory surcharge configured, and the
 * receipt, the ledger and the refund ceiling all follow the authorised number
 * (`INTEGRATIONS.md` §3, `PaymentChargeResult.authorizedAmountCents`).
 *
 * `declineReason` is a class, not the vendor's text: vendor error strings are not shown
 * to patients and are not promised to clients (rules §14.8, §23.8).
 */
export const ChargeSchema = z
    .object({
    id: z.uuid(),
    chargeIntentId: z.uuid(),
    providerTransactionId: z.string().max(120).nullable().default(null),
    amountCents: z.int().min(0),
    /** `authorised - requested` when the processor reported a surcharge, else null. */
    surchargeCents: z.int().nullable().default(null),
    currency: z.string().length(3),
    status: ChargeStatusSchema,
    declineReason: z.string().max(80).nullable().default(null),
    authCode: z.string().max(40).nullable().default(null),
    avsResult: z.string().max(40).nullable().default(null),
    /** The stored-credential flags actually sent (FR-PAY-09). */
    isCustomerInitiated: z.boolean(),
    isRecurring: z.boolean(),
    /** The opaque `BK-…` booking reference. Never a name, never a date. */
    reference: z.string().max(64),
    chargedAt: z.iso.datetime().nullable().default(null),
    settledAt: z.iso.datetime().nullable().default(null),
    createdAt: z.iso.datetime(),
    refundedCents: z.int().min(0).default(0),
})
    .strict();
export const REFUND_STATUSES = ['pending', 'approved', 'declined', 'error'];
export const RefundStatusSchema = z.enum(REFUND_STATUSES);
export const RefundSchema = z
    .object({
    id: z.uuid(),
    chargeId: z.uuid(),
    amountCents: z.int().min(1),
    currency: z.string().length(3),
    reason: z.string().min(1).max(500),
    requestedByUserId: z.string().max(128).nullable().default(null),
    providerTransactionId: z.string().max(120).nullable().default(null),
    status: RefundStatusSchema,
    createdAt: z.iso.datetime(),
})
    .strict();
/* ------------------------------------------------------------- staff requests ---- */
/**
 * `POST /admin/appointments/:id/charge` (M7.5, FR-PAY-04).
 *
 * Both fields are optional and they travel together: omit them and the rule-engine
 * amount on the intent is charged, which is the only thing `clinic_staff` may do
 * without `payments.adjust` (`SECURITY.md` §5.1, the `R` cell). Name an amount and the
 * reason becomes mandatory, in the schema, so a client cannot produce an unexplained
 * override even by accident.
 */
export const ChargeNowRequestSchema = z
    .object({
    amountCents: z.int().min(1).max(10_000_00).optional(),
    reason: z.string().min(3).max(500).optional(),
})
    .strict()
    .refine((value) => value.amountCents === undefined || value.reason !== undefined, {
    message: 'Changing the amount needs a reason.',
    path: ['reason'],
});
/** `POST /admin/appointments/:id/adjust`, `PATCH /admin/charge-intents/:id`. */
export const AdjustChargeIntentRequestSchema = z
    .object({
    amountCents: z.int().min(0).max(10_000_00),
    reason: z.string().min(3).max(500),
})
    .strict();
/** `POST /admin/appointments/:id/void`. Unsettled transactions only. */
export const VoidChargeRequestSchema = z
    .object({
    reason: z.string().min(3).max(500),
    /** Which transaction. Omitted means the intent's latest approved charge. */
    chargeId: z.uuid().optional(),
})
    .strict();
/** `POST /admin/appointments/:id/refund`. Partial is allowed; more than charged is not. */
export const RefundChargeRequestSchema = z
    .object({
    amountCents: z.int().min(1).max(10_000_00),
    reason: z.string().min(3).max(500),
    chargeId: z.uuid().optional(),
})
    .strict();
/**
 * `POST /admin/appointments/:id/fee` (M7.9, FR-SCHED-10).
 *
 * The staff "apply fee" action. The amount defaults to the policy's, and naming a
 * different one needs a reason for the same reason a charge override does.
 */
export const ApplyFeeRequestSchema = z
    .object({
    kind: z.enum(['late_cancel', 'no_show']),
    amountCents: z.int().min(1).max(10_000_00).optional(),
    reason: z.string().min(3).max(500),
    /** Charge it now instead of leaving the intent for the next completion sweep. */
    chargeNow: z.boolean().default(false),
})
    .strict();
/* ------------------------------------------------------------------ responses ---- */
export const ChargeIntentResponseSchema = z
    .object({ data: z.object({ chargeIntent: ChargeIntentSchema }).strict() })
    .strict();
/**
 * What a charge attempt answers.
 *
 * A decline is a `200` carrying `charge.status = 'declined'`, not an error envelope: it
 * is an outcome the clinic has to see and act on, and an error code would make the UI
 * choose between showing the decline reason and showing an error it cannot explain.
 */
export const ChargeResponseSchema = z
    .object({
    data: z.object({ chargeIntent: ChargeIntentSchema, charge: ChargeSchema.nullable() }).strict(),
})
    .strict();
export const VoidResponseSchema = z
    .object({ data: z.object({ charge: ChargeSchema }).strict() })
    .strict();
export const RefundResponseSchema = z
    .object({ data: z.object({ refund: RefundSchema, charge: ChargeSchema }).strict() })
    .strict();
/**
 * `GET /admin/appointments/:id/payments` (M7.5): the whole money history of one visit.
 *
 * One call rather than three because the payment panel renders them together and a
 * panel assembled from three round trips shows three different moments in time - which
 * is how a refunded charge appears next to an intent that still says `charged`.
 */
export const AppointmentPaymentsSchema = z
    .object({
    intents: z.array(ChargeIntentSchema),
    charges: z.array(ChargeSchema),
    refunds: z.array(RefundSchema),
    paymentMethod: PaymentMethodSummarySchema.nullable().default(null),
    /**
     * Everything still refundable, in cents: **settled** charges only, minus what has
     * already been refunded off them.
     *
     * An `approved` charge is deliberately excluded. Before settlement the money can be
     * given back only by voiding, and `ChargesService.refund` refuses the other spelling
     * with `422 INVALID_STATE_TRANSITION` ("It can be voided, not refunded"). Counting
     * an unsettled charge here is what made the panel offer a Refund button whose only
     * possible outcome was that error (`KNOWN_ISSUES.md` 16).
     */
    refundableCents: z.int().min(0).default(0),
    /**
     * Everything still voidable, in cents: `approved`, unsettled charges.
     *
     * The mirror of `refundableCents`, and the two never overlap - a charge is on
     * exactly one side of settlement. A panel offers Void when this is positive and
     * Refund when `refundableCents` is, so the button it shows is always the one the
     * server will accept.
     */
    voidableCents: z.int().min(0).default(0),
})
    .strict();
export const AppointmentPaymentsResponseSchema = z
    .object({ data: AppointmentPaymentsSchema })
    .strict();
export const PaymentMethodListResponseSchema = z
    .object({ data: z.object({ paymentMethods: z.array(PaymentMethodSummarySchema) }).strict() })
    .strict();
/* ------------------------------------------------------------ tenant settings ---- */
/**
 * The payments block of `tenants.settings` (M7.4).
 *
 * `autoChargeOnCompletion` defaults to **true**: ADR-010's whole design is that the
 * card on file is charged when the visit completes, and a clinic that does not want
 * that turns it off deliberately rather than discovering that nothing was ever charged.
 */
export const PaymentSettingsSchema = z
    .object({
    autoChargeOnCompletion: z.boolean().default(true),
    /**
     * Delay between the completion event and the charge, in minutes. Zero charges
     * immediately; a few minutes gives staff time to correct a mis-click before the
     * patient's card is touched.
     */
    autoChargeDelayMinutes: z
        .int()
        .min(0)
        .max(24 * 60)
        .default(0),
})
    .strict();
export const PAYMENT_SETTINGS_DEFAULTS = Object.freeze({
    autoChargeOnCompletion: true,
    autoChargeDelayMinutes: 0,
});
//# sourceMappingURL=payments.js.map